import {
	Col,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Space,
	Switch,
	Tag,
	Typography
} from 'antd'
import React from 'react'
import { useStores } from '../../cls/stores/MobXProvider'
import { observer } from 'mobx-react-lite'
import { countriesOptions, statesOptions } from '../../config/utils'
import { translate as t } from '../../cls/TranslationContext'
import { getGMTOffset } from '../../config'
import FormItem from './FormItem'
const { Paragraph } = Typography

/**
 * BusinessDetailsInput component renders a form for providing business details.
 * It includes fields for account name, phone number, address, city, state, and zip code.
 *
 * @returns {JSX.Element} The rendered BusinessDetailsInput component.
 */
export const BusinessDetailsInput = observer(({ form, formIsDisabled }) => {
	const { users } = useStores()
	const { loggedInUser } = users
	const isSuperAdmin = loggedInUser.isSuperAdmin

	// Get current selected country
	const selectedCountry = Form.useWatch('country', form)

	return (
		<Row>
			<Col flex="auto">
				<Paragraph style={{ color: '#1890ff' }}>
					{t('single.contract.data-location')}
				</Paragraph>

				<Row gutter={16}>
					<Col xs={24} sm={12}>
						<FormItem
							label={t('single.company-name')}
							name="business_name"
						>
							<Input
								name="name"
								autoComplete="name"
								placeholder={t('single.company-name')}
								disabled={formIsDisabled}
							/>
						</FormItem>
					</Col>
					<Col xs={24} sm={12}>
						<FormItem
							label={t('single.registration-number.id')}
							name="business_id"
							tooltip={t('single.registration-number.full')}
						>
							<Input
								placeholder={t('single.registration-number.id')}
								disabled={formIsDisabled}
							/>
						</FormItem>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24} sm={12}>
						<FormItem
							label={t('single.address.line-1')}
							name="address_line_1"
						>
							<Input
								name="address-line1"
								autoComplete="address-line1"
								placeholder={t('single.address.line-1')}
								disabled={formIsDisabled}
							/>
						</FormItem>
					</Col>

					<Col xs={24} sm={12}>
						<FormItem
							label={t('single.address.line-2')}
							name="address_line_2"
						>
							<Input
								name="address-line2"
								autoComplete="address-line2"
								placeholder={t('single.address.line-2')}
								disabled={formIsDisabled}
							/>
						</FormItem>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24} sm={12}>
						<CountrySelect formIsDisabled={formIsDisabled} />
					</Col>

					{/* Make user select state only if selected country is US */}
					{selectedCountry === 'US' && (
						<Col xs={24} sm={12}>
							<FormItem label={t('single.state')} name="state">
								<Select
									name="address-level2"
									autoComplete="address-level2"
									allowClear
									showSearch
									options={statesOptions}
									disabled={formIsDisabled}
								/>
							</FormItem>
						</Col>
					)}

					<Col xs={24} sm={12}>
						<FormItem label={t('single.city')} name="city">
							<Input
								name="address-level2"
								autoComplete="address-level2"
								placeholder={t('single.city')}
								disabled={formIsDisabled}
							/>
						</FormItem>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col xs={24} sm={12}>
						<FormItem label={t('single.zip-code')} name="zip_code">
							<Input
								name="postal-code"
								autoComplete="postal-code"
								placeholder={t('single.zip-code')}
								disabled={formIsDisabled}
							/>
						</FormItem>
					</Col>

					<Col xs={24} sm={12}>
						<FormItem label={t('single.time-zone')} name="timezone">
							<Input
								disabled={formIsDisabled}
								placeholder={`${t(
									'single.time-zone'
								)} (${getGMTOffset()} ${t(
									'single.time-based-gmt'
								)})`}
							/>
						</FormItem>
					</Col>
					<Col xs={24} sm={12}>
						<FormItem label={t('single.phone')}>
							<PhoneInputComponent
								form={form}
								isSuperAdmin={isSuperAdmin}
								formIsDisabled={formIsDisabled}
							/>
						</FormItem>
					</Col>
				</Row>
			</Col>
		</Row>
	)
})

/**
 * CountrySelect component renders a dropdown for selecting a country.
 *
 * @returns {JSX.Element} The rendered CountrySelect component.
 */
export const CountrySelect = observer(({ formIsDisabled }) => {
	return (
		<FormItem label={t('single.country')} name="country">
			<Select
				name="country"
				autoComplete="country"
				allowClear
				showSearch
				options={countriesOptions}
				disabled={formIsDisabled}
				placeholder={t('single.country')}
			/>
		</FormItem>
	)
})

/**
 * PhoneInputComponent renders a phone number input section.
 * It includes fields for phone number prefix and number, along with a status tag and switch.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.value - The current value of the phone input.
 * @param {boolean} props.isSuperAdmin - Indicates if the user is a super admin.
 * @returns {JSX.Element} The rendered PhoneInputComponent.
 */
export const PhoneInputComponent = observer(
	({ form, value = {}, onChange, isSuperAdmin, formIsDisabled }) => {
		const onStatusChange = (status) => {
			void onChange?.({
				...value,
				status
			})
		}
		const phoneStatus = Form.useWatch('status', form)

		return (
			<Row gutter={8} align="middle">
				<Col xs={6} sm={5}>
					<FormItem name="prefix">
						<InputNumber
							name="tel-country-code"
							autoComplete="tel-country-code"
							placeholder={t('single.prefix')}
							style={{ width: '100%' }}
							disabled={formIsDisabled}
						/>
					</FormItem>
				</Col>
				<Col xs={18} sm={10}>
					<FormItem name="number">
						<InputNumber
							name="tel-national"
							autoComplete="tel-national"
							placeholder={t('single.phone-number')}
							style={{ width: '100%' }}
							disabled={formIsDisabled}
						/>
					</FormItem>
				</Col>

				<Col xs={24} sm={9}>
					<FormItem>
						{/* placed this Form Item to maintain the alignment */}
						<Space style={{ width: '100%' }} align="center">
							<Tag color={phoneStatus ? '#52c41a' : '#f5222d'}>
								{phoneStatus
									? t('single.verified')
									: t('single.not-verified')}
							</Tag>
							{isSuperAdmin && (
								<FormItem
									name="status"
									valuePropName="checked"
									noStyle
								>
									<Switch
										onChange={onStatusChange}
										disabled={
											!isSuperAdmin || formIsDisabled
										}
									/>
								</FormItem>
							)}
						</Space>
					</FormItem>
				</Col>
			</Row>
		)
	}
)
